import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Search, Mail, ChevronDown, ChevronRight, Trash2, MapPin,
  AlertCircle, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import JurisdictionPicker from "@/components/JurisdictionPicker";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch (_e) {
    return "—";
  }
}

function ContactRow({ contact, expanded, onToggle, onDelete, onLink }) {
  const [threads, setThreads] = useState(null);
  const [loadingThreads, setLoadingThreads] = useState(false);

  useEffect(() => {
    if (!expanded || threads !== null) return;
    let cancel = false;
    (async () => {
      setLoadingThreads(true);
      try {
        const { data } = await api.get("/admin/ahj/contacts/threads", { params: { email: contact.email } });
        if (!cancel) setThreads(data.threads || []);
      } catch (e) {
        if (!cancel) {
          toast.error("Failed to load threads");
          setThreads([]);
        }
      } finally {
        if (!cancel) setLoadingThreads(false);
      }
    })();
    return () => { cancel = true; };
  }, [expanded, threads, contact.email]);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid={`contact-row-${contact.email}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition text-left"
        data-testid={`contact-expand-${contact.email}`}
      >
        <span className="text-slate-400">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
        <Mail className="w-4 h-4 text-slate-500 flex-none" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm text-slate-900 truncate">{contact.email}</span>
            {contact.is_placeholder && (
              <Badge className="font-mono text-[10px] gap-1 bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-100" data-testid={`contact-placeholder-${contact.email}`}>
                Unverified placeholder
              </Badge>
            )}
            {contact.jurisdiction_label ? (
              <Badge variant="outline" className="font-mono text-[10px] gap-1" data-testid={`contact-juris-${contact.email}`}>
                <MapPin className="w-3 h-3" /> {contact.jurisdiction_label}
              </Badge>
            ) : (
              <Badge variant="outline" className="font-mono text-[10px] text-amber-700 border-amber-200 bg-amber-50">
                Unmatched
              </Badge>
            )}
          </div>
          {contact.name ? (
            <div className="text-xs text-slate-500 mt-0.5 truncate">{contact.name}</div>
          ) : null}
        </div>
        <div className="hidden sm:flex flex-col items-end text-right text-xs text-slate-500">
          <span className="font-mono text-slate-900"><strong>{contact.count}</strong> msgs</span>
          <span>last seen {fmtDate(contact.latest_date_iso)}</span>
        </div>
        <Button
          asChild
          size="sm"
          variant="ghost"
          className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
          onClick={(e) => { e.stopPropagation(); onLink?.(); }}
          data-testid={`contact-link-${contact.email}`}
        >
          <span><MapPin className="w-4 h-4" /></span>
        </Button>
        <Button
          asChild
          size="sm"
          variant="ghost"
          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
          onClick={(e) => { e.stopPropagation(); onDelete(contact.email); }}
          data-testid={`contact-delete-${contact.email}`}
        >
          <span><Trash2 className="w-4 h-4" /></span>
        </Button>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-3" data-testid={`contact-threads-${contact.email}`}>
          {contact.sample_subjects?.length ? (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Sample subjects</div>
              <div className="flex flex-wrap gap-1.5">
                {contact.sample_subjects.map((s, i) => (
                  <Badge key={i} variant="secondary" className="font-normal text-xs">{s}</Badge>
                ))}
              </div>
            </div>
          ) : null}

          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Messages</div>
          {loadingThreads ? (
            <div className="text-xs text-slate-500 py-2 flex items-center gap-2">
              <RefreshCw className="w-3 h-3 animate-spin" /> Loading…
            </div>
          ) : !threads || threads.length === 0 ? (
            <div className="text-xs text-slate-500 py-2">No persisted threads for this contact.</div>
          ) : (
            <ul className="space-y-2">
              {threads.map((t, i) => (
                <li key={i} className="bg-white rounded-md border border-slate-200 p-3" data-testid={`thread-${contact.email}-${i}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="font-medium text-sm text-slate-900 truncate">{t.subject || "(no subject)"}</div>
                    <div className="text-xs text-slate-500 flex-none">{fmtDate(t.date_iso)}</div>
                  </div>
                  {t.snippet ? (
                    <div className="text-xs text-slate-600 mt-1 line-clamp-2">{t.snippet}</div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function AhjContactsManager() {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [jurisdictionFilter, setJurisdictionFilter] = useState("");
  const [jurisdictions, setJurisdictions] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [pickerFor, setPickerFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (stateFilter) params.state = stateFilter;
      if (jurisdictionFilter) params.jurisdiction_fips = jurisdictionFilter;
      const { data } = await api.get("/admin/ahj/contacts", { params });
      setContacts(data.contacts || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [q, stateFilter, jurisdictionFilter]);

  const loadJurisdictions = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ahj/contacts/jurisdictions");
      setJurisdictions(data.jurisdictions || []);
    } catch (_e) { /* non-fatal */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadJurisdictions(); }, [loadJurisdictions]);

  const states = useMemo(() => {
    const set = new Set();
    jurisdictions.forEach(j => { if (j.state_abbr) set.add(j.state_abbr); });
    return Array.from(set).sort();
  }, [jurisdictions]);

  const filteredJurisdictions = useMemo(() => {
    if (!stateFilter) return jurisdictions;
    return jurisdictions.filter(j => j.state_abbr === stateFilter);
  }, [jurisdictions, stateFilter]);

  const matchedCount = contacts.filter(c => c.jurisdiction_label).length;
  const unmatchedCount = contacts.length - matchedCount;

  const handleDelete = async (email) => {
    if (!window.confirm(`Delete contact ${email} and all stored messages? This can't be undone.`)) return;
    try {
      await api.delete("/admin/ahj/contacts", { params: { email } });
      toast.success("Contact removed");
      load();
      loadJurisdictions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6" data-testid="ahj-contacts-manager">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h3 className="font-outfit text-lg font-semibold text-slate-900">AHJ Contacts</h3>
          <p className="text-xs text-slate-500 mt-1">
            Contacts extracted from prior .mbox uploads, auto-linked to a jurisdiction.{" "}
            <span className="font-mono text-slate-700">{matchedCount} matched · {unmatchedCount} unmatched · {total} total</span>
          </p>
        </div>
        <Button
          variant="outline" size="sm" onClick={() => { load(); loadJurisdictions(); }}
          disabled={loading} data-testid="contacts-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? "animate-spin" : ""}`} />Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 mb-4">
        <div className="sm:col-span-6 relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            type="search"
            placeholder="Search email / name / subject…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="contacts-search-input"
          />
        </div>
        <div className="sm:col-span-3">
          <select
            value={stateFilter}
            onChange={(e) => { setStateFilter(e.target.value); setJurisdictionFilter(""); }}
            className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm bg-white"
            data-testid="contacts-state-filter"
          >
            <option value="">All states</option>
            {states.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="sm:col-span-3">
          <select
            value={jurisdictionFilter}
            onChange={(e) => setJurisdictionFilter(e.target.value)}
            className="w-full h-10 border border-slate-300 rounded-md px-3 text-sm bg-white"
            data-testid="contacts-juris-filter"
          >
            <option value="">All jurisdictions</option>
            {filteredJurisdictions
              .filter(j => j.jurisdiction_fips)
              .map(j => (
                <option key={j.jurisdiction_fips} value={j.jurisdiction_fips}>
                  {j.jurisdiction_label} ({j.contacts})
                </option>
              ))}
          </select>
        </div>
      </div>

      {/* Empty state / list */}
      {!loading && contacts.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center" data-testid="contacts-empty">
          <Mail className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-600">No contacts match these filters.</div>
          <div className="text-xs text-slate-500 mt-1">
            {total === 0 ? "Upload a Gmail Takeout .mbox above to begin building your AHJ contact graph." : "Try clearing filters."}
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="contacts-list">
          {contacts.map((c) => (
            <ContactRow
              key={c.email}
              contact={c}
              expanded={expanded === c.email}
              onToggle={() => setExpanded(expanded === c.email ? null : c.email)}
              onDelete={handleDelete}
              onLink={() => setPickerFor(c)}
            />
          ))}
        </div>
      )}

      <JurisdictionPicker
        open={!!pickerFor}
        contact={pickerFor}
        onClose={() => setPickerFor(null)}
        onResolved={(picked) => {
          toast.success(`Linked ${pickerFor?.email} → ${picked.label}`);
          load();
        }}
      />

      {unmatchedCount > 0 && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-none mt-0.5" />
          <div>
            <strong>{unmatchedCount} contacts</strong> couldn't be auto-linked to a jurisdiction. Re-running with the latest counties_db, or uploading a fresh .mbox with richer subject lines, can improve matching.
          </div>
        </div>
      )}
    </div>
  );
}
